import { createAsyncThunk } from "@reduxjs/toolkit";
import { MANAGER_CALL_ACTIONS } from "@regal-voice/shared-types";
import { AnyAction } from "redux";
import { ThunkDispatch } from "redux-thunk";

// TODO Why is this imported from Pages?
import { ManagerAction } from "Pages/liveOps/Shared/Components/TaskDetails/Components/ManagerCallControls/ManagerCallControlConstants";
import {
    startManagerAction as callStartManagerAction,
    stopManagerAction as callStopManagerAction,
} from "Services/ConversationsApiService";
import {
    postMessageToParentWithManagerActionIfNeeded,
    type ManagerActionEventKey,
} from "Services/embed/CrossDomainMessenger";
import { renderErrorMessage } from "Services/LoggingService";
import { RVTaskInstance } from "Types/TaskTypes/RVTaskInstance";

import { selectAgentActivity, selectAgentInformation } from "../agent/AgentInformationSlice";
import { selectBrandAvailableStatuses } from "../brand";
import { clearConference, setConference } from "../conferences/ConferenceStateSlice";
import { selectConference } from "../conferences/Selectors";
import { clearManagerAction as clearManagerActionReducer, setActionData, setLoading } from "./ManagerActionStateSlice";
import { selectActiveAction, selectCallSid } from "./Selectors";

import type { ThunkAction } from "../StoreThunkTypes";

const MANAGER_ACTION_EVENT_KEYS: Record<ManagerAction, ManagerActionEventKey> = {
    barge: "started-barging",
    listen: "started-listening",
    whisper: "started-whispering",
    takeOver: "started-manager-takeover",
};

const ACTION_ERROR_MESSAGES: Record<ManagerAction, string> = {
    barge: "The barge did not work.",
    listen: "Could not start listening.",
    whisper: "Could not start whispering.",
    takeOver: "Could not start taking over.",
};

function getActionErrorMessage(action: ManagerAction, isAiAgentCall: boolean): string {
    if (action === "barge" && isAiAgentCall) {
        return "The take over did not work.";
    }
    return ACTION_ERROR_MESSAGES[action];
}

export const startManagerAction = createAsyncThunk<
    boolean,
    { managerAction: ManagerAction; taskSid: string; taskInfo: RVTaskInstance; isAiAgentCall?: boolean },
    { state: RootState; dispatch: ThunkDispatch<RootState, unknown, AnyAction> }
>(
    "managerAction/start",
    async ({ managerAction, taskSid, taskInfo, isAiAgentCall = false }, { getState, dispatch }) => {
        dispatch(setLoading(managerAction));
        const availableStatuses = selectBrandAvailableStatuses(getState());
        const currentAgentActivity = selectAgentActivity(getState());
        const { workerUri, workerSid } = selectAgentInformation(getState());
        const callSid = selectCallSid(getState());

        if (!workerUri || !workerSid) {
            dispatch(setLoading(null));
            renderErrorMessage({
                content: `Failed to start ${managerAction} call`,
                extraData: {
                    managerAction,
                    taskSid,
                    taskInfo,
                    isAiAgentCall,
                    workerUri,
                    workerSid,
                },
                logAsWarn: true,
            });
            return false;
        }

        const statusToUse =
            // When performing a manager takeover on an AI agent's call, the admin needs to get a task. To get a task, they need to be available.
            // If they're already available, there's no need to use a different status. If not, we'll just default to the standard "Available"
            managerAction === MANAGER_CALL_ACTIONS.takeOver
                ? currentAgentActivity.available
                    ? currentAgentActivity.name
                    : "Available"
                : (availableStatuses.find((s) => !s.available)?.name ?? "Offline");

        let actionResponse;
        try {
            // we send callSid only for the case when we want to switch from one manager action to another, otherwise it's null
            actionResponse = await callStartManagerAction({
                taskSid,
                managerContactUri: workerUri,
                action: managerAction,
                twilioWorkerSid: workerSid,
                // TODO: Open a separate PR to remove sending status once it's been proven stable that we can compute it on the BE
                status: statusToUse,
                callSid,
            });
        } catch (error) {
            dispatch(setLoading(null));
            renderErrorMessage({
                content: getActionErrorMessage(managerAction, isAiAgentCall),
                extraData: {
                    error,
                    managerAction,
                    taskSid,
                    taskInfo,
                    isAiAgentCall,
                    workerUri,
                    workerSid,
                },
            });
            return false;
        }

        if (taskInfo) {
            postMessageToParentWithManagerActionIfNeeded(
                {
                    contactPhone: taskInfo.contactPhone,
                    externalId: taskInfo.contactExternalId,
                    profileId: taskInfo.profileId,
                    taskType: taskInfo.attributes.title || taskInfo.type,
                    agentEmail: taskInfo.attributes.acceptedByEmail,
                },
                MANAGER_ACTION_EVENT_KEYS[managerAction]
            );
        }

        dispatch(setConference({ connected: true }));
        dispatch(setActionData({ activeAction: actionResponse.action, callSid: actionResponse.callSid, taskSid }));
        dispatch(setLoading(null));

        return true;
    }
);

export const stopManagerAction = createAsyncThunk<
    void,
    string,
    { state: RootState; dispatch: ThunkDispatch<RootState, unknown, AnyAction> }
>("managerAction/stop", async (taskSid: string, { getState, dispatch }) => {
    const currentAction = selectActiveAction(getState());
    dispatch(setLoading(currentAction));
    const callSid = selectCallSid(getState());
    const conference = selectConference(getState());

    if (!callSid || !conference?.connected) {
        dispatch(setLoading(null));
        return renderErrorMessage({ content: "Call not found" });
    }

    try {
        await callStopManagerAction(taskSid, callSid);
        // update type to unknown and use normalizeError
    } catch {
        dispatch(setLoading(null));
        return renderErrorMessage({ content: `Unable to stop to ${currentAction}` });
    }

    dispatch(clearConference());
    dispatch(clearManagerActionReducer());
});

export function clearManagerAction(): ThunkAction<void, RootState, any, AnyAction> {
    return function thunk(dispatch: ThunkDispatch<RootState, unknown, AnyAction>): void {
        dispatch(clearConference());
        dispatch(clearManagerActionReducer());
    };
}
